import React from 'react';
import { SparkReferenceValue } from 'types/SparkStepReference';
import { useNodeSpecificOperations } from './useNodeSpecificOperations';

const BOARD_NODE_TYPE_KEYS = {
  TEXT: 'text',
  GRAPH: 'graph',
  MERMAID: 'mermaid',
};

type UseReferencePopupType = {
  disabled?: boolean;
};

export const useReferencePopup = ({
  disabled = false,
}: UseReferencePopupType) => {
  const [lastFocusedElement, setLastFocusedElement] =
    React.useState<HTMLElement | null>(null);
  const [anchor, setAnchor] = React.useState<HTMLElement | null>(null);
  const { insertReferenceInCodePanel, insertReferenceInTextField } =
    useNodeSpecificOperations();

  const getLastFocusedElement = (
    event:
      | React.MouseEvent<HTMLElement, MouseEvent>
      | React.MouseEvent<HTMLDivElement, MouseEvent>,
    id: string
  ) => {
    const contentEL = document.getElementById(id);
    if (!contentEL) {
      return;
    }
    setAnchor(contentEL);

    const el = event.target as HTMLElement;
    if (!el) {
      return;
    }

    const shouldSetEL =
      el.hasAttribute('contenteditable') || // editor js
      el.hasAttribute('data-empty') || //editor js
      el.classList.contains('ͼe') || // graph node - for correctly formatted value
      el.classList.contains('ͼd') || // graph node - for incorrectly formatted value
      el.classList.contains('cm-line') || // graph node - for the line
      el.closest('.ProseMirror') !== null || // Tiptap editor
      el.classList.contains('notion-style-editor') || // Tiptap editor class
      el.classList.contains('ProseMirror'); // Direct Tiptap editor element

    if (shouldSetEL) {
      setLastFocusedElement(el);
      return;
    }

    // If we clicked inside a Tiptap editor container but not directly on an editable element,
    // find the editor element and focus it
    const tiptapContainer = el.closest('.notion-style-editor-container');
    if (tiptapContainer) {
      const editorElement = tiptapContainer.querySelector('.ProseMirror');
      if (editorElement && editorElement instanceof HTMLElement) {
        setLastFocusedElement(editorElement);
        // Focus the editor to ensure proper cursor placement
        editorElement.focus();
        return;
      }
    }
  };

  const insertTextAtCursor = async (
    reference: SparkReferenceValue,
    nodeType: string
  ) => {
    const isNodeWithCodePanel =
      nodeType === BOARD_NODE_TYPE_KEYS.MERMAID ||
      nodeType === BOARD_NODE_TYPE_KEYS.GRAPH;
    const selection = window.getSelection();
    if (!selection || !lastFocusedElement || !selection?.anchorNode) return;

    if (isNodeWithCodePanel) {
      insertReferenceInCodePanel(lastFocusedElement, reference);

      return;
    }

    if (
      lastFocusedElement &&
      lastFocusedElement.tagName === 'TEXTAREA' &&
      lastFocusedElement instanceof HTMLTextAreaElement
    ) {
      insertReferenceInTextField(lastFocusedElement, reference);
      setLastFocusedElement(null);
      return;
    }

    if (
      lastFocusedElement.contains(selection.anchorNode) ||
      lastFocusedElement === selection.anchorNode
    ) {
      const span = document.createElement('span');
      span.innerText = reference.reference;
      span.classList.add('editable-template');
      span.setAttribute('contenteditable', 'false'); // Prevent editing the template

      const selection = window.getSelection();
      const range = selection?.getRangeAt(0);

      if (range) {
        range.deleteContents();
        range.insertNode(span);
        range.setStartAfter(span);
        range.setEndAfter(span);
        selection?.removeAllRanges();
        selection?.addRange(range);
      }
    } else {
      if (!selection || selection.rangeCount === 0) return;
      const range = selection.getRangeAt(0);
      let cursorNode = range.startContainer;
      let cursorOffset = range.startOffset;
      // Get the original text
      const originalText = cursorNode.textContent || '';

      const updatedText =
        originalText.slice(0, cursorOffset) +
        reference.reference +
        originalText.slice(cursorOffset);

      cursorNode.textContent = updatedText;
    }
    lastFocusedElement.focus();
    lastFocusedElement.blur();
  };

  const closePopover = () => {
    setLastFocusedElement(null);
  };

  const isPopOverOver = !disabled && !!lastFocusedElement;

  return {
    insertTextAtCursor,
    getLastFocusedElement,
    isPopOverOver,
    anchor,
    closePopover,
  };
};
